import { useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { startDemoRun } from '@wundo/client/auth.ts';
import { m } from '@/paraglide/messages.js';
import { SignInLanding } from '@/components/SignInLanding.tsx';
import { leaveToLoginError } from '@/lib/leaveToLoginError.ts';

/*
 * Where the "try a roleplay" demo e-mail lands. It sits outside `_app`: the
 * visitor has no account, only the pass in the URL.
 *
 * A pass has a run budget, so the mail cannot point at the backend directly —
 * the scanners and sandboxes that read the mail first would empty it. The run
 * is claimed by a POST behind the button on this page, and a pass that is
 * spent or unknown is answered with the marketing page rather than a refusal.
 */
export const Route = createFileRoute('/demo/start')({
  component: DemoStartRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    pass: typeof search.pass === 'string' ? search.pass : '',
  }),
});

/* Reading the search is the route's job, so the screen itself stays renderable. */
function DemoStartRoute() {
  const { pass } = Route.useSearch();

  return <DemoStartPage pass={pass} />;
}

export interface DemoStartPageProps {
  pass: string;
}

export function DemoStartPage({ pass }: DemoStartPageProps) {
  const claim = useCallback(() => startDemoRun(pass), [pass]);

  return (
    <SignInLanding
      title={m.demo_start_title()}
      body={m.demo_start_body()}
      button={m.demo_start_continue()}
      action={claim}
      onRefused={leaveToLoginError}
    />
  );
}
